import { ROSELoader } from './rose';
import { BinaryReader } from '../util/binaryreader';
import { BinaryWriter } from '../util/binarywriter';

export class DataTable {
  reader: any = null;
  rowCount: number = 0;
  columnCount: number = 0;
  offsets: number[] = [];
  rowCache: any = {};
  rowHeight: number = 0;
  dataOffset: number = 0;
  /** Header, column metadata, and row labels retained byte-for-byte. */
  sourcePrefix: Uint8Array | null = null;

  row(rowIdx: number): string[] {
    if (this.rowCache[rowIdx]) {
      return this.rowCache[rowIdx];
    }

    this.reader.seek(this.offsets[rowIdx]);

    const row: string[] = [];
    for (let i = 0; i < this.columnCount; ++i) {
      row.push(this.reader.readUint16Str());
    }

    this.rowCache[rowIdx] = row;
    return row;
  }

  item(rowIdx: number, columnIdx: number): string {
    return this.row(rowIdx)[columnIdx];
  }

  setRow(rowIdx: number, values: string[]): void {
    if (rowIdx < 0 || rowIdx >= this.rowCount) {
      throw new RangeError(`STB row ${rowIdx} is outside 0..${this.rowCount - 1}`);
    }
    if (values.length !== this.columnCount) {
      throw new Error(
        `STB row requires ${this.columnCount} columns, got ${values.length}`,
      );
    }
    this.rowCache[rowIdx] = [...values];
  }

  static parse(rh: BinaryReader): DataTable {
    const data = new DataTable();
    const magic = rh.readStrLen(4);
    if (magic !== 'STB1') throw 'Unsupported STB magic header ' + magic;
    const offset  = rh.readUint32();
    const rows    = rh.readUint32();
    const columns = rh.readUint32();
    data.rowHeight = rh.readUint32();
    data.dataOffset = offset;
    data.sourcePrefix = rh.slice(0, offset);
    data.reader = rh;
    data.rowCount = rows - 1;
    data.columnCount = columns - 1;
    rh.seek(offset);
    data.rowCache = {};
    data.offsets = [];
    for (let i = 0; i < data.rowCount; ++i) {
      data.offsets.push(rh.tell());
      for (let j = 0; j < data.columnCount; ++j) {
        rh.skip(rh.readUint16());
      }
    }
    return data;
  }

  /**
   * Rewrite cell data while preserving the original STB column metadata and
   * row-label section exactly. Adding rows/columns needs a separate schema-aware
   * table builder; native map registration can safely fill an existing row.
   */
  static serialize(data: DataTable): ArrayBuffer {
    if (!data.sourcePrefix || data.sourcePrefix.length !== data.dataOffset) {
      throw new Error('STB serialization requires the original table header');
    }
    const writer = new BinaryWriter(data.sourcePrefix.length + 1024);
    writer.writeBytes(data.sourcePrefix);
    for (let rowIndex = 0; rowIndex < data.rowCount; rowIndex++) {
      const row = data.row(rowIndex);
      if (row.length !== data.columnCount) {
        throw new Error(
          `STB row ${rowIndex} requires ${data.columnCount} columns, got ${row.length}`,
        );
      }
      for (const value of row) writer.writeUint16Str(value ?? '');
    }
    return writer.toArrayBuffer();
  }

  static load(path: any, callback: any) {
    ROSELoader.load(path, function(rh: any) {
      callback(DataTable.parse(rh));
    });
  }
}
